import { For, Show, type Component } from 'solid-js';

import { damageTypeClass, damageTypeLabel } from '../game/damage';
import type { GameStore } from '../state/store';

/**
 * Damage numbers rising off whatever got hit.
 *
 * Plain DOM nodes anchored to projected 3D points. They are removed by the
 * store's TTL sweep rather than by an animation callback, so a number can never
 * outlive the entity it belongs to. Damage the local player takes falls and
 * reads red; damage they deal rises and reads gold, so the direction of a fight
 * is legible without reading a single digit.
 *
 * An elemental hit says so, in colour **and** in a word. It has to: the number the
 * server sends is already mitigated, so a fire hit halved by fire resistance is the
 * same digits as an ordinary one, and a player has no other way to learn which
 * resistance a zone is asking them to gear for. Physical carries neither — it is the
 * overwhelming majority of hits, and marking it would leave the elemental ones with
 * nothing to stand out against.
 */
export const FloatingDamageOverlay: Component<{ store: GameStore }> = (props) => {
  // As in Nameplates: the list depends only on damageNumbers(), and each number
  // reads its own anchor, so a frame does not re-key the whole <For>.
  return (
    <div class="floating-damage" data-testid="floating-damage">
      <For each={props.store.damageNumbers()}>
        {(d) => {
          const anchor = () => props.store.anchors()[d.entityId];
          const element = () => damageTypeClass(d.damageType);
          const label = () => damageTypeLabel(d.damageType);
          return (
            <Show when={anchor()?.visible}>
              <span
                class="damage-number"
                classList={{
                  'damage-number--self': d.entityId === props.store.selfId(),
                  // A crit is the server's decision, arriving on the event. Drawn
                  // larger and warmer, because a number that is 1.5x bigger than the
                  // last one is not by itself readable as "that was a crit".
                  'damage-number--crit': d.crit,
                  // Null for physical, so the base gold and the `--self` red keep
                  // saying which direction the fight is going.
                  ...(element() === null ? {} : { [element() as string]: true }),
                }}
                data-damage-type={d.damageType}
                data-testid={`damage-${d.id}`}
                style={{ left: `${anchor()?.x ?? 0}px`, top: `${anchor()?.y ?? 0}px` }}
              >
                {d.amount}
                {/*
                  The mana half of a shielded blow, drawn beside the health half rather than added
                  into it. Phase 39: `amount` is the health lost, so a shielded hit already reads as
                  the small number it was — this is what says *why* it was small, and it is the
                  difference between a mechanic working and mana leaking for no reason.
                */}
                <Show when={d.absorbed > 0}>
                  <span class="damage-number__absorbed" data-testid={`absorbed-${d.id}`}>
                    +{d.absorbed}
                  </span>
                </Show>
                <Show when={d.crit}>
                  <span class="damage-number__crit" aria-hidden="true">
                    !
                  </span>
                </Show>
                <Show when={label() !== null}>
                  <span class="damage-number__element">{label()}</span>
                </Show>
              </span>
            </Show>
          );
        }}
      </For>
    </div>
  );
};
